import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CadastroSimplesSection, { parseDecimalInput } from '../components/CadastroSimplesSection';
import {
  createTipoCimento,
  deleteTipoCimento,
  getTipoCimento,
  updateTipoCimento,
} from '../services/tipoCimentoService';
import {
  createTipoConcreto,
  deleteTipoConcreto,
  getTipoConcreto,
  updateTipoConcreto,
} from '../services/tipoConcretoService';
import {
  createTipoCorpoProva,
  deleteTipoCorpoProva,
  getTipoCorpoProva,
  updateTipoCorpoProva,
} from '../services/tipoCorpoProvaService';
import {
  createTipoCarga,
  deleteTipoCarga,
  getTipoCarga,
  updateTipoCarga,
} from '../services/tipoCargaService';
import {
  createEtapaObra,
  deleteEtapaObra,
  getEtapaObra,
  updateEtapaObra,
} from '../services/etapaObraService';
import {
  createPrensa,
  deletePrensa,
  getPrensa,
  updatePrensa,
} from '../services/prensaService';
import { getEmpresa } from '../services/empresaService';
import { getSessionRole } from '../services/authSession';

// Indica se o registro é do catálogo padrão da Kretos (nato) ou de um cliente específico.
function resolveNato(item) {
  const idCliente = item.idCliente ?? item.id_cliente ?? null;
  return Boolean(item.flagNato ?? item.flag_nato ?? idCliente == null);
}

// Campos de catálogo compartilhados por todas as abas: origem (nato x da empresa) e, nas abas em que
// o ponderador é calculado pela equipe, o status de validação.
// `comSomenteLeitura` só é usado pelas abas já conectadas ao backend (cimento, concreto e etapa de obra),
// onde o registro nato pertence à Kretos e o PUT/DELETE responde 404. As abas ainda pendentes
// (carga e corpo de prova) mantêm a saída antiga, sem essa dimensão.
// AJUSTE BACKEND AQUI: `flagValidado` ainda não existe em tipoCarga e tipoCorpoProva. Sem ele, tudo que
// já está cadastrado é tratado como em vigor, para não marcar registro antigo como pendente indevidamente.
function resolveCatalogo(item, { comValidacao = false, comSomenteLeitura = false } = {}) {
  const nato = resolveNato(item);
  // Textos derivados: alimentam a busca das colunas que são exibidas como badge.
  const base = { nato, origem: nato ? 'Padrão Kretos' : 'Da empresa' };
  if (comSomenteLeitura) base.somenteLeitura = nato;
  if (!comValidacao) return base;

  const validado = nato || Boolean(item.flagValidado ?? item.flag_validado ?? true);
  return { ...base, validado, status: validado ? 'Em vigor' : 'Aguardando validação' };
}

// Badge de origem/status usado nas colunas de catálogo (nato x da empresa, em vigor x pendente).
function BadgeCadastro({ texto, destaque }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${destaque ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
        }`}
    >
      {texto}
    </span>
  );
}

// Normaliza um tipo de cimento da API para o formato da tela.
function normalizeTipoCimento(item) {
  return {
    id: String(item.idTipoCimento || item.id || `tci-${Date.now()}-${Math.random()}`),
    nome: item.nome || item.nomeTipoCimento || '',
    ponderador: item.ponderador ?? item.valorPonderador ?? '',
    ...resolveCatalogo(item, { comSomenteLeitura: true }),
  };
}

// Normaliza um tipo de concreto da API para o formato da tela.
function normalizeTipoConcreto(item) {
  return {
    id: String(item.idTipoConcreto || item.id_tipo_concreto || item.id || `tco-${Date.now()}-${Math.random()}`),
    nome: item.nomeTipoConcreto || item.nome_tipo_concreto || item.descricao || item.nome || '',
    ...resolveCatalogo(item, { comSomenteLeitura: true }),
  };
}

// Normaliza um tipo de corpo de prova da API para o formato da tela.
// AJUSTE BACKEND AQUI: campos `observacao` e `flagValidado` precisam existir na API de tipoCorpoProva.
function normalizeTipoCorpoProva(item) {
  return {
    id: String(item.idTipoCorpoProva || item.id || `tcp-${Date.now()}-${Math.random()}`),
    nome: item.nomeTipoCorpoProva || '',
    ponderador: item.valrPonderadorCalculo ?? '',
    observacao: item.observacao || '',
    ...resolveCatalogo(item, { comValidacao: true }),
  };
}

// Normaliza um tipo de carga da API para o formato da tela.
// AJUSTE BACKEND AQUI: campos `observacao` e `flagValidado` precisam existir na API de tipoCarga.
function normalizeTipoCarga(item) {
  return {
    id: String(item.idTipoCarga || item.id_tipo_carga || item.id || `tca-${Date.now()}-${Math.random()}`),
    nome: item.nome || item.nomeTipoCarga || '',
    ponderador: item.ponderador ?? item.valrPonderador ?? '',
    observacao: item.observacao || item.obsTipoCarga || '',
    ...resolveCatalogo(item, { comValidacao: true }),
  };
}

// Normaliza uma etapa de obra da API para o formato da tela.
// Atenção: as duas condições são independentes — `somenteLeitura` vem de idCliente (registro da Kretos,
// que responde 404 no PUT/DELETE) e `todasEmpresas` vem de idEmpresa (só afeta o rótulo). Uma etapa do
// cliente sem empresa vale para todas as empresas dele e continua editável.
function normalizeEtapaObra(item) {
  const idEmpresa = item.idEmpresa ?? item.id_empresa ?? item.empresa?.idEmpresa ?? '';
  // O backend ainda devolve uma empresa só; a tela já trabalha com lista para quando o vínculo virar
  // N:N — aí a API passa a mandar `empresas`/`idEmpresas` e nada mais muda por aqui.
  const listaApi = item.idEmpresas ?? item.empresas;
  const idEmpresas = Array.isArray(listaApi)
    ? listaApi.map((empresa) => String(empresa?.idEmpresa ?? empresa?.id_empresa ?? empresa))
    : (idEmpresa === '' || idEmpresa == null ? [] : [String(idEmpresa)]);
  return {
    id: String(item.idEtapaObra || item.id_etapa_obra || item.id || `eto-${Date.now()}-${Math.random()}`),
    nome: item.nomeEtapaObra || item.nome_etapa_obra || item.descricao || item.nome || '',
    idEmpresas,
    todasEmpresas: idEmpresas.length === 0,
    // A razão social já vem embutida na resposta; a lista de empresas é só fallback.
    nomeEmpresa: item.empresa?.razaoSocial || item.empresa?.razao_social || '',
    ...resolveCatalogo(item, { comSomenteLeitura: true }),
  };
}

// Normaliza uma prensa da API para o formato da tela.
// A prensa é o equipamento do cliente; o ponderador usado no Fcj vem do tipo de carga vinculado.
function normalizePrensa(item) {
  const idTipoCarga = item.idTipoCarga
    ?? item.id_tipo_carga
    ?? item.tipoCarga?.idTipoCarga
    ?? item.tipoCarga?.id_tipo_carga
    ?? '';
  return {
    id: String(item.idPrensa || item.id_prensa || item.id || `pre-${Date.now()}-${Math.random()}`),
    identificacao: item.codgIdentificacao || item.codg_identificacao || item.nome || '',
    idTipoCarga: idTipoCarga === '' || idTipoCarga == null ? '' : String(idTipoCarga),
    // Fallback de exibição: o nome já vem embutido quando a API devolve o tipo de carga aninhado.
    nomeTipoCarga: item.tipoCarga?.nomeTipoCarga || item.tipoCarga?.nome || '',
  };
}

// Normaliza um tipo de carga da API para opção de select.
function normalizeTipoCargaOption(item) {
  return {
    value: String(item.idTipoCarga || item.id_tipo_carga || item.id || ''),
    label: item.nome || item.nomeTipoCarga || 'Tipo de carga sem nome',
  };
}

// Normaliza uma empresa da API para opção de select.
function normalizeEmpresaOption(item) {
  return {
    value: String(item.idEmpresa || item.id_empresa || item.id || ''),
    label: item.razaoSocial || item.nomeEmpresa || item.nome_empresa || item.nome || 'Empresa sem nome',
  };
}

const MENSAGENS_CIMENTO = {
  criado: 'Tipo de cimento cadastrado com sucesso.',
  atualizado: 'Tipo de cimento atualizado com sucesso.',
  excluido: 'Tipo de cimento excluído com sucesso.',
  confirmarExclusao: 'Você tem certeza que deseja excluir este tipo de cimento?',
  vazio: 'Nenhum tipo de cimento encontrado.',
  erroSalvar: 'Não foi possível salvar o tipo de cimento.',
  erroExcluir: 'Não foi possível excluir o tipo de cimento.',
};

const MENSAGENS_CONCRETO = {
  criado: 'Tipo de concreto cadastrado com sucesso.',
  atualizado: 'Tipo de concreto atualizado com sucesso.',
  excluido: 'Tipo de concreto excluído com sucesso.',
  confirmarExclusao: 'Você tem certeza que deseja excluir este tipo de concreto?',
  vazio: 'Nenhum tipo de concreto encontrado.',
  erroSalvar: 'Não foi possível salvar o tipo de concreto.',
  erroExcluir: 'Não foi possível excluir o tipo de concreto.',
};

const MENSAGENS_CORPO_PROVA = {
  criado: 'Tipo de corpo de prova cadastrado com sucesso.',
  atualizado: 'Tipo de corpo de prova atualizado com sucesso.',
  excluido: 'Tipo de corpo de prova excluído com sucesso.',
  confirmarExclusao: 'Você tem certeza que deseja excluir este tipo de corpo de prova?',
  vazio: 'Nenhum tipo de corpo de prova encontrado.',
  erroSalvar: 'Não foi possível salvar o tipo de corpo de prova.',
  erroExcluir: 'Não foi possível excluir o tipo de corpo de prova.',
};

const MENSAGENS_CARGA = {
  criado: 'Tipo de carga cadastrado com sucesso.',
  atualizado: 'Tipo de carga atualizado com sucesso.',
  excluido: 'Tipo de carga excluído com sucesso.',
  confirmarExclusao: 'Você tem certeza que deseja excluir este tipo de carga?',
  vazio: 'Nenhum tipo de carga encontrado.',
  erroSalvar: 'Não foi possível salvar o tipo de carga.',
  erroExcluir: 'Não foi possível excluir o tipo de carga.',
};

const MENSAGENS_PRENSA = {
  criado: 'Prensa cadastrada com sucesso.',
  atualizado: 'Prensa atualizada com sucesso.',
  excluido: 'Prensa excluída com sucesso.',
  confirmarExclusao: 'Você tem certeza que deseja excluir esta prensa?',
  vazio: 'Nenhuma prensa encontrada.',
  erroSalvar: 'Não foi possível salvar a prensa.',
  erroExcluir: 'Não foi possível excluir a prensa.',
};

const MENSAGENS_ETAPA_OBRA = {
  criado: 'Etapa da obra cadastrada com sucesso.',
  atualizado: 'Etapa da obra atualizada com sucesso.',
  excluido: 'Etapa da obra excluída com sucesso.',
  confirmarExclusao: 'Você tem certeza que deseja excluir esta etapa da obra?',
  vazio: 'Nenhuma etapa da obra encontrada.',
  erroSalvar: 'Não foi possível salvar a etapa da obra.',
  erroExcluir: 'Não foi possível excluir a etapa da obra.',
};

// Nas abas com validação o cliente solicita: as mensagens falam em "solicitação", não em cadastro efetivado.
function buildMensagensCliente(mensagens, entidade) {
  return {
    ...mensagens,
    criado: `Solicitação enviada. ${entidade} entra em vigor após a validação da equipe Kretos.`,
    atualizado: 'Solicitação atualizada com sucesso.',
    erroSalvar: 'Não foi possível enviar a solicitação.',
  };
}

const MENSAGENS_CARGA_CLIENTE = buildMensagensCliente(MENSAGENS_CARGA, 'O tipo de carga');
const MENSAGENS_CORPO_PROVA_CLIENTE = buildMensagensCliente(MENSAGENS_CORPO_PROVA, 'O tipo de corpo de prova');

const COLUNA_ORIGEM = {
  name: 'origem',
  label: 'Origem',
  somenteLeitura: true,
  render: (item) => <BadgeCadastro texto={item.origem} />,
};

const COLUNA_STATUS = {
  name: 'status',
  label: 'Status',
  somenteLeitura: true,
  render: (item) => <BadgeCadastro texto={item.status} destaque={item.validado} />,
};

// Faixa aceita para o coeficiente "S" do tipo de cimento.
const PONDERADOR_CIMENTO_MIN = 0.2;
const PONDERADOR_CIMENTO_MAX = 0.85;

const CAMPOS_CIMENTO = [
  { name: 'nome', label: 'Nome', required: true },
  {
    name: 'ponderador',
    label: 'Ponderador',
    type: 'decimal',
    required: true,
    min: PONDERADOR_CIMENTO_MIN,
    max: PONDERADOR_CIMENTO_MAX,
    placeholder: 'Ex: 0,25',
    hint: 'Deve estar entre 0,2 e 0,85.',
  },
  COLUNA_ORIGEM,
];

// Campos das abas em que o ponderador é calculado pela equipe (carga e corpo de prova):
// o admin define o valor e valida; o cliente só descreve o equipamento e aguarda.
function buildCamposComValidacao({ isAdmin, entidade, placeholderNome, placeholderPonderador, hintPonderador, labelObservacao, placeholderObservacao }) {
  const nome = { name: 'nome', label: 'Nome', required: true, placeholder: placeholderNome };

  if (isAdmin) {
    return [
      nome,
      {
        name: 'ponderador',
        label: 'Ponderador',
        type: 'decimal',
        required: true,
        placeholder: placeholderPonderador,
        hint: `${hintPonderador} Calculado pela equipe.`,
      },
      {
        name: 'observacao',
        label: 'Observação',
        colunaLabel: 'Observação',
        type: 'textarea',
        placeholder: 'Dados informados pelo cliente, laudo de calibração, etc.',
      },
      COLUNA_ORIGEM,
      COLUNA_STATUS,
      {
        name: 'validado',
        label: `Validado — liberar ${entidade} para uso nos ensaios`,
        type: 'checkbox',
        apenasFormulario: true,
        hint: 'Enquanto não estiver validado, o registro fica visível para o cliente como pendente e não entra em vigor.',
      },
    ];
  }

  return [
    nome,
    { name: 'ponderador', label: 'Ponderador', somenteLeitura: true },
    {
      name: 'observacao',
      label: labelObservacao,
      colunaLabel: 'Observação',
      type: 'textarea',
      required: true,
      generoFeminino: true,
      placeholder: placeholderObservacao,
      hint: 'Esses dados são o que a equipe usa para calcular o ponderador.',
    },
    COLUNA_ORIGEM,
    COLUNA_STATUS,
  ];
}

const CAMPOS_CARGA = {
  admin: buildCamposComValidacao({
    isAdmin: true,
    entidade: 'este tipo de carga',
    placeholderPonderador: 'Ex: 1,00',
    hintPonderador: 'Fator que converte a carga de ruptura (Ton) em tensão (MPa).',
  }),
  cliente: buildCamposComValidacao({
    isAdmin: false,
    placeholderNome: 'Ex: Anel dinamométrico 50t',
    labelObservacao: 'Observação / dados do equipamento',
    placeholderObservacao: 'Descreva a prensa, a célula de carga e a calibração do equipamento.',
  }),
};

const CAMPOS_CORPO_PROVA = {
  admin: buildCamposComValidacao({
    isAdmin: true,
    entidade: 'este tipo de corpo de prova',
    placeholderPonderador: 'Ex: 1,00 (h = 2d, padrão NBR 5739)',
    hintPonderador: 'Fator de correção de esbeltez (relação altura/diâmetro do CP).',
  }),
  cliente: buildCamposComValidacao({
    isAdmin: false,
    placeholderNome: 'Ex: Cilíndrico 10 x 20 cm',
    labelObservacao: 'Observação / dimensões do corpo de prova',
    placeholderObservacao: 'Informe o diâmetro e a altura do corpo de prova e o molde utilizado.',
  }),
};

// Monta o payload de tipo de cimento. O ponderador "S" é obrigatório e validado na faixa 0,2–0,85.
function buildTipoCimentoPayload(form) {
  return {
    nomeTipoCimento: form.nome.trim(),
    valorPonderador: parseDecimalInput(form.ponderador),
  };
}

// Monta o payload de tipo de concreto.
function buildTipoConcretoPayload(form) {
  return {
    nomeTipoConcreto: form.nome.trim(),
    idEmpresa: form.idEmpresa ? Number(form.idEmpresa) : null,
  };
}

// Monta o payload das abas com validação: o admin define ponderador e libera; o cliente envia
// a solicitação sem ponderador, sempre pendente.
// AJUSTE BACKEND AQUI: campos `observacao` e `flagValidado` precisam existir nas APIs correspondentes.
function buildPayloadComValidacao({ isAdmin, chaveNome, chavePonderador }) {
  return (form) => (isAdmin
    ? {
      [chaveNome]: form.nome.trim(),
      [chavePonderador]: parseDecimalInput(form.ponderador),
      observacao: String(form.observacao || '').trim() || null,
      flagValidado: Boolean(form.validado),
    }
    : {
      [chaveNome]: form.nome.trim(),
      [chavePonderador]: null,
      observacao: String(form.observacao || '').trim(),
      flagValidado: false,
    });
}

const PAYLOAD_CARGA = {
  admin: buildPayloadComValidacao({ isAdmin: true, chaveNome: 'nomeTipoCarga', chavePonderador: 'valrPonderador' }),
  cliente: buildPayloadComValidacao({ isAdmin: false, chaveNome: 'nomeTipoCarga', chavePonderador: 'valrPonderador' }),
};

const PAYLOAD_CORPO_PROVA = {
  admin: buildPayloadComValidacao({ isAdmin: true, chaveNome: 'nomeTipoCorpoProva', chavePonderador: 'valrPonderadorCalculo' }),
  cliente: buildPayloadComValidacao({ isAdmin: false, chaveNome: 'nomeTipoCorpoProva', chavePonderador: 'valrPonderadorCalculo' }),
};

// Monta o payload de prensa: identificação do equipamento e o tipo de carga que ele usa.
function buildPrensaPayload(form) {
  return {
    codgIdentificacao: form.identificacao.trim(),
    idTipoCarga: form.idTipoCarga ? Number(form.idTipoCarga) : null,
  };
}

// Monta o payload de etapa de obra. Sem empresa, a etapa vale para todas as empresas do cliente.
// `idEmpresa` (a primeira da lista) continua indo enquanto o backend aceita só uma empresa por etapa;
// quando o N:N subir, é só remover essa chave e ficar com `idEmpresas`.
function buildEtapaObraPayload(form) {
  const idEmpresas = (Array.isArray(form.idEmpresas) ? form.idEmpresas : [])
    .map((valor) => Number(valor))
    .filter((valor) => Number.isFinite(valor));
  return {
    nomeEtapaObra: form.nome.trim(),
    idEmpresas,
    idEmpresa: idEmpresas.length ? idEmpresas[0] : null,
  };
}

const ABAS = [
  { id: 'cimento', label: 'Tipos de cimento' },
  { id: 'concreto', label: 'Tipos de concreto' },
  { id: 'corpo-prova', label: 'Tipos de corpo de prova' },
  { id: 'carga', label: 'Tipos de carga' },
  { id: 'prensa', label: 'Prensas' },
  { id: 'etapa-obra', label: 'Etapas da obra' },
];

// Tela agregadora dos cadastros técnicos: catálogos pequenos que alimentam os selects
// das telas de concretagem e ensaio. Cada aba é um CRUD independente.
function CadastrosTecnicosPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [tipoCargaOptions, setTipoCargaOptions] = useState([]);
  const [loadingTipoCargas, setLoadingTipoCargas] = useState(true);

  const abaParam = searchParams.get('aba');
  const abaAtiva = ABAS.some((aba) => aba.id === abaParam) ? abaParam : ABAS[0].id;
  // Só a equipe (admin) define ponderador e valida os cadastros pedidos pelos clientes.
  const isAdmin = getSessionRole() === 'admin';
  const perfil = isAdmin ? 'admin' : 'cliente';

  useEffect(() => {
    let isMounted = true;
    const loadEmpresas = async () => {
      try {
        const data = await getEmpresa();
        if (!isMounted) return;
        setEmpresaOptions(Array.isArray(data) ? data.map(normalizeEmpresaOption) : []);
      } catch (error) {
        if (isMounted) setEmpresaOptions([]);
      } finally {
        if (isMounted) setLoadingEmpresas(false);
      }
    };
    loadEmpresas();
    return () => { isMounted = false; };
  }, []);

  // Opções da aba de prensas: o tipo de carga é o que carrega o ponderador usado no Fcj.
  useEffect(() => {
    let isMounted = true;
    const loadTipoCargas = async () => {
      try {
        const data = await getTipoCarga();
        if (!isMounted) return;
        setTipoCargaOptions(Array.isArray(data) ? data.map(normalizeTipoCargaOption).filter((opt) => opt.value) : []);
      } catch (error) {
        if (isMounted) setTipoCargaOptions([]);
      } finally {
        if (isMounted) setLoadingTipoCargas(false);
      }
    };
    loadTipoCargas();
    return () => { isMounted = false; };
  }, []);

  const camposConcreto = useMemo(() => ([
    { name: 'nome', label: 'Nome', required: true, placeholder: 'Ex: Convencional' },
    /*{
      name: 'idEmpresa',
      label: 'Empresa',
      colunaLabel: 'Empresa',
      type: 'select',
      // A equipe pode deixar em branco para criar um tipo nato, válido para todas as empresas.
      required: !isAdmin,
      generoFeminino: true,
      options: empresaOptions,
      placeholder: isAdmin ? 'Todas as empresas (tipo nato)' : 'Selecione a empresa',
      hint: isAdmin
        ? 'Sem empresa, o tipo vira parte do catálogo padrão e fica disponível para todas.'
        : 'O tipo fica disponível apenas para as concretagens da empresa selecionada.',
      render: (item) => {
        if (item.nato) return <span className="text-slate-500">Todas as empresas</span>;
        const option = empresaOptions.find((opt) => String(opt.value) === String(item.idEmpresa));
        return option ? option.label : '-';
      },
    }, */
    COLUNA_ORIGEM,
  ]), [/*empresaOptions, isAdmin*/]);

  const camposPrensa = useMemo(() => ([
    {
      name: 'identificacao',
      label: 'Identificação',
      colunaLabel: 'Identificação',
      required: true,
      generoFeminino: true,
      placeholder: 'Ex: Prensa 01',
      hint: 'Como a prensa aparece nos resultados dos corpos de prova.',
    },
    {
      name: 'idTipoCarga',
      label: 'Tipo de carga',
      colunaLabel: 'Tipo de carga',
      type: 'select',
      required: true,
      options: tipoCargaOptions,
      placeholder: 'Selecione o tipo de carga',
      hint: 'Vem da aba "Tipos de carga". É o ponderador desse tipo que entra no cálculo do Fcj.',
      render: (item) => {
        const option = tipoCargaOptions.find((opt) => String(opt.value) === String(item.idTipoCarga));
        return option?.label || item.nomeTipoCarga || '-';
      },
    },
  ]), [tipoCargaOptions]);

  const camposEtapaObra = useMemo(() => ([
    { name: 'nome', label: 'Nome', required: true, placeholder: 'Ex: Fundação' },
    {
      name: 'idEmpresas',
      label: 'Empresas',
      colunaLabel: 'Empresas',
      type: 'multiselect',
      // A equipe pode deixar em branco; a etapa passa a valer para todas as empresas do cliente.
      required: !isAdmin,
      mensagemObrigatorio: 'Marque ao menos uma empresa.',
      options: empresaOptions,
      placeholder: 'Selecione as empresas',
      labelTodas: 'Todas as empresas',
      vazio: 'Nenhuma empresa cadastrada.',
      hint: isAdmin
        ? 'Marque quantas empresas quiser. Sem nenhuma marcada, a etapa fica disponível para todas as empresas do cliente.'
        : 'Marque quantas empresas quiser: a etapa fica disponível nas concretagens de todas elas.',
      render: (item) => {
        if (item.todasEmpresas) return <span className="text-slate-500">Todas as empresas</span>;
        const nomes = item.idEmpresas
          .map((id) => empresaOptions.find((opt) => String(opt.value) === String(id))?.label)
          .filter(Boolean);
        if (nomes.length) return nomes.join(', ');
        return item.nomeEmpresa || '-';
      },
    },
    COLUNA_ORIGEM,
  ]), [empresaOptions, isAdmin]);

  // Registro nato é da Kretos: o backend responde 404 no PUT/DELETE, então nem o admin edita.
  // O `isAdmin` precisa entrar aqui porque a prop substitui o controle interno da seção.
  const podeGerenciarItem = (item) => isAdmin && !item.somenteLeitura;

  const handleAbaChange = (id) => {
    setSearchParams(id === ABAS[0].id ? {} : { aba: id }, { replace: true });
  };

  return (
    <div className="cadastros-tecnicos-page space-y-6">
      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Cadastros técnicos</h2>
        <p className="mt-2 max-w-3xl text-sm text-slate-500">
          Catálogos técnicos usados nas telas de concretagem e ensaio: tipos de cimento, de concreto,
          de corpo de prova, de carga, as prensas e as etapas da obra. Cada catálogo tem os valores padrão da Kretos
          e o que a sua empresa cadastrar fica visível só para ela.
        </p>
      </section>

      <div className="overflow-x-auto">
        <div className="flex min-w-max gap-2 rounded-full border border-slate-200 bg-white p-1 shadow-sm">
          {ABAS.map((aba) => (
            <button
              key={aba.id}
              type="button"
              onClick={() => handleAbaChange(aba.id)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${abaAtiva === aba.id
                ? 'bg-[#0e7490]/10 text-[#0e7490]'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
            >
              {aba.label}
            </button>
          ))}
        </div>
      </div>

      {abaAtiva === 'cimento' ? (
        <CadastroSimplesSection
          titulo="Tipos de cimento"
          descricao={isAdmin ?
            'Catálogo padrão da Kretos e personalizações próprias.\nO ponderador (coeficiente "S") está relacionado com a taxa de crescimento da resistência no decorrer das idades, devendo ficar entre 0,2 e 0,85.'
            : 'O catálogo padrão já vem pronto para uso. Sua empresa pode cadastrar tipos próprios, informando o ponderador (coeficiente "S") entre 0,2 e 0,85.'}
          labelNovo="Cadastrar novo tipo de cimento"
          labelEdicao="Editando tipo de cimento"
          mensagens={MENSAGENS_CIMENTO}
          campos={CAMPOS_CIMENTO}
          listar={getTipoCimento}
          criar={createTipoCimento}
          atualizar={updateTipoCimento}
          excluir={deleteTipoCimento}
          normalizar={normalizeTipoCimento}
          montarPayload={buildTipoCimentoPayload}
          podeEditar={podeGerenciarItem}
          podeExcluir={podeGerenciarItem}
        />
      ) : null}

      {abaAtiva === 'concreto' ? (
        <CadastroSimplesSection
          titulo="Tipos de concreto"
          descricao={'Catálogo padrão e personalização própria.\nO tipo de concreto será selecionado no cadastro de Caminhões.'}
          /*descricao={isAdmin
            ? 'Catálogo padrão e personalização própria.\nO tipo de concreto será selecionado no cadastro de Caminhões.'
            : 'O catálogo padrão já vem pronto para uso. Sua empresa pode cadastrar tipos próprios, visíveis apenas para ela.'}*/
          labelNovo="Cadastrar novo tipo de concreto"
          labelEdicao="Editando tipo de concreto"
          mensagens={MENSAGENS_CONCRETO}
          campos={camposConcreto}
          listar={getTipoConcreto}
          criar={createTipoConcreto}
          atualizar={updateTipoConcreto}
          excluir={deleteTipoConcreto}
          normalizar={normalizeTipoConcreto}
          montarPayload={buildTipoConcretoPayload}
          podeEditar={podeGerenciarItem}
          podeExcluir={podeGerenciarItem}
        />
      ) : null}

      {abaAtiva === 'corpo-prova' ? (
        <CadastroSimplesSection
          titulo="Tipos de corpo de prova"
          descricao={'Catálogo padrão da Kretos e personalização própria.\nO ponderador está relacionado com a forma e tipo de esforço ensaiado e adequado para estrutura Kretos.\nInformar detalhadamente sobre a forma do corpo de prova e sobre o esforço a ser ensaiado para a Kretos aplicar o ponderador adequado.'}
          labelNovo={isAdmin ? 'Cadastrar novo tipo de corpo de prova' : 'Solicitar novo tipo de corpo de prova'}
          labelEdicao={isAdmin ? 'Editando tipo de corpo de prova' : 'Editando solicitação'}
          labelSalvar={isAdmin ? 'Adicionar' : 'Enviar para validação'}
          avisoFormulario={isAdmin
            ? null
            : 'O ponderador é calculado pela equipe Kretos. O tipo de corpo de prova solicitado fica como "Aguardando validação" e só entra em vigor depois que a equipe libera.'}
          mensagens={isAdmin ? MENSAGENS_CORPO_PROVA : MENSAGENS_CORPO_PROVA_CLIENTE}
          campos={CAMPOS_CORPO_PROVA[perfil]}
          listar={getTipoCorpoProva}
          criar={createTipoCorpoProva}
          atualizar={updateTipoCorpoProva}
          excluir={deleteTipoCorpoProva}
          normalizar={normalizeTipoCorpoProva}
          montarPayload={PAYLOAD_CORPO_PROVA[perfil]}
          labelAcaoEditar={(item) => (item.validado ? 'Editar' : 'Validar')}
        />
      ) : null}

      {abaAtiva === 'carga' ? (
        <CadastroSimplesSection
          titulo="Tipos de carga"
          descricao={isAdmin
            ? 'Catálogo padrão da Kretos mais os tipos solicitados pelos clientes. Defina o ponderador (fator de área, usado no cálculo do Fcj) e valide para o tipo entrar em vigor.'
            : 'O catálogo padrão já vem pronto para uso. Sua empresa pode solicitar novos tipos de carga: a equipe Kretos calcula o ponderador e libera o uso.'}
          labelNovo={isAdmin ? 'Cadastrar novo tipo de carga' : 'Solicitar novo tipo de carga'}
          labelEdicao={isAdmin ? 'Editando tipo de carga' : 'Editando solicitação'}
          labelSalvar={isAdmin ? 'Adicionar' : 'Enviar para validação'}
          avisoFormulario={isAdmin
            ? null
            : 'O ponderador é calculado pela equipe Kretos. O tipo de carga solicitado fica como "Aguardando validação" e só entra em vigor depois que a equipe libera.'}
          mensagens={isAdmin ? MENSAGENS_CARGA : MENSAGENS_CARGA_CLIENTE}
          campos={CAMPOS_CARGA[perfil]}
          listar={getTipoCarga}
          criar={createTipoCarga}
          atualizar={updateTipoCarga}
          excluir={deleteTipoCarga}
          normalizar={normalizeTipoCarga}
          montarPayload={PAYLOAD_CARGA[perfil]}
          labelAcaoEditar={(item) => (item.validado ? 'Editar' : 'Validar')}
        />
      ) : null}

      {abaAtiva === 'prensa' ? (
        <CadastroSimplesSection
          titulo="Prensas"
          descricao={'Prensas usadas nos ensaios de compressão.\nCada prensa é vinculada a um tipo de carga, e é o ponderador desse tipo que entra no cálculo do Fcj.'}
          labelNovo="Cadastrar nova prensa"
          labelEdicao="Editando prensa"
          mensagens={MENSAGENS_PRENSA}
          campos={camposPrensa}
          listar={getPrensa}
          criar={createPrensa}
          atualizar={updatePrensa}
          excluir={deletePrensa}
          normalizar={normalizePrensa}
          montarPayload={buildPrensaPayload}
          carregando={loadingTipoCargas}
        />
      ) : null}

      {abaAtiva === 'etapa-obra' ? (
        <CadastroSimplesSection
          titulo="Etapas da obra"
          descricao={isAdmin
            ? 'Catálogo padrão da Kretos e as personalizações próprias.\nEla deve ser selecionada no cadastro do Caminhão.'
            : 'O catálogo padrão já vem pronto para uso. Sua empresa pode cadastrar etapas próprias, vinculadas a uma das suas empresas.'}
          labelNovo="Cadastrar nova etapa da obra"
          labelEdicao="Editando etapa da obra"
          mensagens={MENSAGENS_ETAPA_OBRA}
          campos={camposEtapaObra}
          listar={getEtapaObra}
          criar={createEtapaObra}
          atualizar={updateEtapaObra}
          excluir={deleteEtapaObra}
          normalizar={normalizeEtapaObra}
          montarPayload={buildEtapaObraPayload}
          podeEditar={podeGerenciarItem}
          podeExcluir={podeGerenciarItem}
          carregando={loadingEmpresas}
        />
      ) : null}
    </div>
  );
}

export default CadastrosTecnicosPage;
